import { useEffect, useMemo, useRef, useState } from 'react';
import { MonacoJsonEditor } from '../../components/MonacoJsonEditor';
import { ToolFormField as Field } from './ToolFormField';
import styles from './ToolForm.module.css';

interface JsonFieldProps {
  label: string;
  hint?: string;
  value: unknown;
  onChange: (parsed: unknown) => void;
  validateValue?: (parsed: unknown) => string | null;
  onValidityChange?: (isValid: boolean) => void;
  onEdit?: () => void;
  height: string;
}

/** Raw JSON editor that preserves user text and reports parse and semantic validity. */
export const JsonField = ({
  label,
  hint,
  value,
  onChange,
  validateValue,
  onValidityChange,
  onEdit,
  height,
}: JsonFieldProps) => {
  const incomingCanonical = useMemo(() => JSON.stringify(value), [value]);
  const [text, setText] = useState(() => JSON.stringify(value, null, 2));
  const [error, setError] = useState<string | null>(null);

  // Only reseed when the value changes externally, not when a parent echoes our own write.
  const lastReportedCanonicalRef = useRef(incomingCanonical);

  useEffect(() => {
    if (incomingCanonical !== lastReportedCanonicalRef.current) {
      lastReportedCanonicalRef.current = incomingCanonical;
      setText(JSON.stringify(value, null, 2));
      setError(null);
      onValidityChange?.(true);
    }
  }, [incomingCanonical, value, onValidityChange]);

  const handleChange = (next: string) => {
    onEdit?.();
    setText(next);
    let parsed: unknown;
    try {
      parsed = JSON.parse(next);
    } catch {
      setError('Invalid JSON — fix before saving.');
      onValidityChange?.(false);
      return;
    }
    const validationError = validateValue?.(parsed);
    if (validationError) {
      setError(validationError);
      onValidityChange?.(false);
      return;
    }
    lastReportedCanonicalRef.current = JSON.stringify(parsed);
    setError(null);
    onValidityChange?.(true);
    onChange(parsed);
  };

  return (
    <Field label={label} hint={hint}>
      <MonacoJsonEditor value={text} onChange={handleChange} height={height} />
      {error && (
        <p className={styles.jsonError} role="alert">
          {error}
        </p>
      )}
    </Field>
  );
};
